/**
 * Options de configuration pour la comparaison d'objets
 */
export interface CompareOptions {
  /** Inclure les champs qui ont été supprimés (présents dans reference mais pas dans updated) */
  includeDeleted?: boolean;
  /** Profondeur maximale pour la comparaison récursive (évite les références circulaires) */
  maxDepth?: number;
  /** Champs à ignorer lors de la comparaison */
  excludeFields?: string[];
  /** Comparaison stricte (=== vs ==) */
  strictComparison?: boolean;
  /**
   * Considère null, undefined et chaîne vide comme équivalents. Utile quand la
   * référence vient d'un mapping API (nulls) et les valeurs d'un formulaire
   * (défauts "") : un champ non touché ne doit pas compter comme modifié.
   */
  looseEmptyComparison?: boolean;
}

/**
 * Compare deux objets du même type et retourne un objet contenant uniquement
 * les champs qui ont été modifiés (différents entre reference et updated)
 * 
 * @template T - Le type des objets à comparer
 * @param reference - L'objet de référence (valeurs originales)
 * @param updated - L'objet avec les valeurs modifiées
 * @param options - Options de configuration pour la comparaison
 * @returns Un objet partiel contenant uniquement les champs modifiés
 */
export function getUpdatedFields<T extends Record<string, any>>(
  reference: T,
  updated: T,
  options: CompareOptions = {}
): Partial<T> {
  const {
    includeDeleted = true,
    maxDepth = 10,
    excludeFields = [],
    strictComparison = true,
    looseEmptyComparison = false
  } = options;

  const isEmptyValue = (val: any) => val === null || val === undefined || val === '';

  const changes: Partial<T> = {};

  // Fonction utilitaire pour comparer deux valeurs en profondeur
  const isEqual = (val1: any, val2: any, currentDepth: number = 0): boolean => {
    // Protection contre la récursion infinie
    if (currentDepth > maxDepth) return true;

    // Comparaison stricte ou non stricte
    const compareFunction = strictComparison
      ? (a: any, b: any) => a === b
      : (a: any, b: any) => a == b;

    // Cas des valeurs identiques
    if (compareFunction(val1, val2)) return true;

    // Vides équivalents (null / undefined / "") si demandé
    if (looseEmptyComparison && isEmptyValue(val1) && isEmptyValue(val2)) return true;

    // Cas des valeurs null/undefined
    if (val1 == null || val2 == null) return val1 == val2;

    // Cas des dates
    if (val1 instanceof Date && val2 instanceof Date) {
      return val1.getTime() === val2.getTime();
    }

    // Cas des arrays
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      return val1.every((item, index) => isEqual(item, val2[index], currentDepth + 1));
    }

    // Cas des objets (mais pas les dates, arrays, etc.)
    if (
      typeof val1 === 'object' &&
      typeof val2 === 'object' &&
      val1 !== null &&
      val2 !== null &&
      !Array.isArray(val1) &&
      !Array.isArray(val2) &&
      !(val1 instanceof Date) &&
      !(val2 instanceof Date)
    ) {
      const keys1 = Object.keys(val1);
      const keys2 = Object.keys(val2);

      // Comparer les clés
      const allKeys = new Set([...keys1, ...keys2]);

      return Array.from(allKeys).every(key =>
        isEqual(val1[key], val2[key], currentDepth + 1)
      );
    }

    // Cas des valeurs primitives
    return compareFunction(val1, val2);
  };

  // Obtenir toutes les clés uniques des deux objets
  const allKeys = new Set([
    ...Object.keys(reference || {}),
    ...Object.keys(updated || {})
  ]);

  // Parcourir toutes les clés
  allKeys.forEach(key => {
    // Ignorer les champs exclus
    if (excludeFields.includes(key)) return;

    const referenceValue = reference?.[key];
    const updatedValue = updated?.[key];

    // Vérifier si les valeurs sont différentes
    if (!isEqual(referenceValue, updatedValue)) {
      // Cas où le champ existe dans updated (modification ou ajout)
      if (key in (updated || {})) {
        (changes as any)[key] = updatedValue;
      }
      // Cas où le champ existe seulement dans reference (suppression)
      else if (includeDeleted && key in (reference || {})) {
        (changes as any)[key] = undefined;
      }
    }
  });

  return changes;
}

/**
 * Version avancée qui retourne des métadonnées détaillées sur les changements
 */
export function getDetailedChanges<T extends Record<string, any>>(
  reference: T,
  updated: T,
  options: CompareOptions = {}
) {
  const changes = getUpdatedFields(reference, updated, options);
  const changedFields = Object.keys(changes);

  // Analyser les types de changements
  const added: string[] = [];
  const updated_fields: string[] = [];
  const deleted: string[] = [];

  changedFields.forEach(field => {
    const inReference = field in (reference || {});
    const inUpdated = field in (updated || {});
    const isUndefined = (changes as any)[field] === undefined;

    if (!inReference && inUpdated) {
      added.push(field);
    } else if (inReference && !inUpdated && isUndefined) {
      deleted.push(field);
    } else {
      updated_fields.push(field);
    }
  });

  return {
    hasChanges: changedFields.length > 0,
    changes,
    summary: {
      total: changedFields.length,
      added: added.length,
      updated: updated_fields.length,
      deleted: deleted.length
    },
    fields: {
      all: changedFields,
      added,
      updated: updated_fields,
      deleted
    }
  };
}

/**
 * Fonction utilitaire pour créer un patch qui peut être appliqué à un objet
 */
export function createPatch<T extends Record<string, any>>(
  reference: T,
  updated: T,
  options: CompareOptions = {}
): {
  patch: Partial<T>;
  apply: (target: T) => T;
  canApply: (target: T) => boolean;
} {
  const patch = getUpdatedFields(reference, updated, options);

  return {
    patch,
    apply: (target: T): T => ({
      ...target,
      ...patch
    }),
    canApply: (target: T): boolean => {
      // Vérifier si le patch peut être appliqué de manière cohérente
      return Object.keys(patch).every(key => {
        const targetValue = target[key];
        const referenceValue = reference[key];
        // Le patch peut être appliqué si la valeur cible correspond à la référence
        return targetValue === referenceValue || (targetValue == null && referenceValue == null);
      });
    }
  };
}

// Exemples d'utilisation avec différents types
/*

// Exemple 1: Avec votre type BorrowerLoanFormValues
const originalLoan = {
  loanAmount: "100000",
  loanType: "IMMOBILIER",
  interestRate: 2.5,
  tiers: [{ duration: 12, amount: 1000 }]
};

const updatedLoan = {
  ...originalLoan,
  loanAmount: "120000", // Modifié
  interestRate: 3.0,    // Modifié
  insuranceAmount: 500  // Ajouté
};

const changes1 = getUpdatedFields(originalLoan, updatedLoan);

// Exemple 2: Avec un type utilisateur
interface User {
  id: number;
  name: string;
  email: string;
  preferences?: {
    theme: string;
    notifications: boolean;
  };
}

const originalUser: User = {
  id: 1,
  name: "John",
  email: "john@example.com",
  preferences: { theme: "light", notifications: true }
};

const updatedUser: User = {
  ...originalUser,
  name: "John Doe", // Modifié
  preferences: { theme: "dark", notifications: true } // Modifié
};

const changes2 = getUpdatedFields(originalUser, updatedUser);
const detailed = getDetailedChanges(originalUser, updatedUser);

// Exemple 3: Avec options personnalisées
const changesWithOptions = getUpdatedFields(originalUser, updatedUser, {
  excludeFields: ['id'], // Ignorer l'ID
  includeDeleted: false, // Ne pas inclure les suppressions
  strictComparison: false // Comparaison non stricte (== au lieu de ===)
});

// Exemple 4: Création et application d'un patch
const { patch, apply, canApply } = createPatch(originalUser, updatedUser);

if (canApply(originalUser)) {
  const patchedUser = apply(originalUser);
  console.log('Patch appliqué:', patchedUser);
}

*/
